import { createSimpleStore } from 'react-simple-reducer'
import api from '../api'
import { QuestaoModel, TIPO_QUESTAO } from '../models/QuestaoModel'
import { QuestaoAlternativaModel } from '../models/QuestaoAlternativaModel'
import { toast } from 'react-toastify'

export interface IQuestaoEditorState {
  [questaoItem: string]: {
    questaoOriginal?: QuestaoModel
    questao?: QuestaoModel
    hasChange?: boolean
    isLoading?: boolean
    erro?: string
    submitted?: boolean
  }
}

const initialState: IQuestaoEditorState = {}

function getQuestoesAlternativas(questao?: Partial<QuestaoModel>) {
  return questao?.questoesAlternativas || []
}

function changeQuestaoItem(
  state: IQuestaoEditorState,
  questaoId: string,
  novaQuestao: Partial<QuestaoModel>,
  novaQuestaoItem: any = { hasChange: true }
): void {
  const questaoItem = state[questaoId] || {
    questao: { questoesAlternativas: [] },
    isLoading: false,
    hasChange: false,
  }
  const questaoAtual = questaoItem.questao || { questoesAlternativas: [] }
  const questao = { ...questaoAtual, ...novaQuestao }
  questao.questoesAlternativas = getQuestoesAlternativas(questao) as any
  state[questaoId] = { ...questaoItem, questao, ...novaQuestaoItem }
}

const questaoEditorStore = createSimpleStore(
  initialState,
  {
    setTipoQuestao(state, payload: { questaoId: string; tipoQuestao: TIPO_QUESTAO }) {
      const textoQuestao = state[payload.questaoId]?.questao?.textoQuestao || ''
      changeQuestaoItem(state, payload.questaoId, { textoQuestao, tipoQuestao: payload.tipoQuestao })
    },
    setModuloId(state, payload: { questaoId: string; moduloId: number }) {
      changeQuestaoItem(state, payload.questaoId, { moduloId: payload.moduloId })
    },
    setLocal(state, payload: { questaoId: string; local: any }) {
      changeQuestaoItem(state, payload.questaoId, { local: payload.local })
    },
    setEnunciado(state, payload: { questaoId: string; textoQuestao: string }) {
      changeQuestaoItem(state, payload.questaoId, { textoQuestao: payload.textoQuestao })
    },
    setConfiguracaoAdicional(state, payload: { questaoId: string; configuracaoAdicional: string }) {
      changeQuestaoItem(state, payload.questaoId, { configuracaoAdicional: payload.configuracaoAdicional })
    },
    addAlternativa(state, payload: { questaoId: string }) {
      const questaoAtual = state[payload.questaoId]?.questao || { questoesAlternativas: [] }
      let infoAdicionalAlternativa = {}
      switch (questaoAtual.tipoQuestao) {
        case TIPO_QUESTAO.RELACIONAR_ITEM_A_IMAGEM:
          infoAdicionalAlternativa = { respostaEsperada: '{"top": 50, "left": 50}' }
          break
        case TIPO_QUESTAO.MULTIPLA_ESCOLHA:
          infoAdicionalAlternativa = { respostaEsperada: 'false' }
          break
        default:
          break
      }
      const novaQuestaoAlternativa: QuestaoAlternativaModel = {
        id: Math.random() - 1,
        ...infoAdicionalAlternativa,
      }
      const questoesAlternativas = [
        ...getQuestoesAlternativas(questaoAtual),
        novaQuestaoAlternativa,
      ]
      changeQuestaoItem(state, payload.questaoId, { questoesAlternativas })
    },
    changeAlternativas(state, payload: { questaoId: string; questoesAlternativas: QuestaoAlternativaModel[] }) {
      changeQuestaoItem(state, payload.questaoId, { questoesAlternativas: payload.questoesAlternativas })
    },
    changeAlternativaById(state, payload: { questaoId: string; alternativa: QuestaoAlternativaModel }) {
      const questoesAlternativas = getQuestoesAlternativas(state[payload.questaoId]?.questao).map((x) =>
        x.id === payload.alternativa.id ? payload.alternativa : x
      )
      changeQuestaoItem(state, payload.questaoId, { questoesAlternativas })
    },
    changeConteudoAlternativa(state, payload: { questaoId: string; conteudoAlternativa: string; alternativaId: number }) {
      const alternativa = getQuestoesAlternativas(state[payload.questaoId]?.questao).find(
        (x) => x.id === payload.alternativaId
      )
      if (alternativa) {
        alternativa.conteudoAlternativa = payload.conteudoAlternativa
      }
      if (state[payload.questaoId]) state[payload.questaoId].hasChange = true
    },
    changeRespostaEsperada(state, payload: { questaoId: string; respostaEsperada: string; alternativaId: number }) {
      const alternativa = getQuestoesAlternativas(state[payload.questaoId]?.questao).find(
        (x) => x.id === payload.alternativaId
      )
      if (alternativa) {
        alternativa.respostaEsperada = payload.respostaEsperada
      }
      if (state[payload.questaoId]) state[payload.questaoId].hasChange = true
    },
    setFeedback(state, payload: { alternativaId: number; questaoId: string; feedback: string }) {
      const alternativa = getQuestoesAlternativas(state[payload.questaoId]?.questao).find(
        (x) => x.id === payload.alternativaId
      )
      if (alternativa) {
        alternativa.feedback = payload.feedback
      }
      if (state[payload.questaoId]) state[payload.questaoId].hasChange = true
    },
    deleteAlternativaById(state, payload: { questaoId: string; alternativaId: number }) {
      const questoesAlternativas = getQuestoesAlternativas(state[payload.questaoId]?.questao).filter(
        (x) => x.id !== payload.alternativaId
      )
      changeQuestaoItem(state, payload.questaoId, { questoesAlternativas })
    },
    resetQuestao(state, payload: { questaoId: string }) {
      const questaoOriginal = state[payload.questaoId]?.questaoOriginal || { questoesAlternativas: [] }
      state[payload.questaoId] = {
        isLoading: false,
        hasChange: false,
        questao: {
          ...questaoOriginal,
          questoesAlternativas: getQuestoesAlternativas(questaoOriginal),
        },
      }
    },
    setSubmittedFalse(state, payload: { questaoId: string }) {
      state[payload.questaoId].submitted = false
    },
    pushQuestaoItem(state, payload: { questaoId: string }) {
      state[payload.questaoId] = { questao: { questoesAlternativas: [] }, isLoading: false, hasChange: false }
    },
    clearQuestaoItem(state, payload: { questaoId: string }) {
      delete state[payload.questaoId]
    },
    questaoFetchStarted(state, payload: { questaoId: string }) {
      changeQuestaoItem(state, payload.questaoId, {}, { isLoading: true })
    },
    questaoFetchSuccess(state, payload: { questaoId: string; questao: QuestaoModel }) {
      const questao = {
        ...payload.questao,
        questoesAlternativas: getQuestoesAlternativas(payload.questao),
      }
      state[payload.questaoId] = {
        questao,
        questaoOriginal: questao,
        isLoading: false,
        erro: undefined,
        hasChange: false
      }
    },
    questaoFetchError(state, payload: { questaoId: string; erro: string }) {
      changeQuestaoItem(state, payload.questaoId, {}, { isLoading: false, erro: payload.erro })
    },
    questaoCreateOrUpdateStarted(state, payload: { questaoId: string }) {
      changeQuestaoItem(state, payload.questaoId, {}, { isLoading: true })
    },
    questaoCreateOrUpdateSuccess(state, payload: { questaoId: string; questao: QuestaoModel }) {
      const questao = {
        ...payload.questao,
        questoesAlternativas: getQuestoesAlternativas(payload.questao),
      }
      state[payload.questaoId] = {
        questao,
        questaoOriginal: questao,
        isLoading: false,
        erro: undefined,
        submitted: true,
        hasChange: false
      }
    },
    questaoCreateOrUpdateError(state, payload: { questaoId: string; erro: string }) {
      toast(payload.erro)
      changeQuestaoItem(state, payload.questaoId, {}, { isLoading: false, erro: payload.erro })
    },
    questaoDeleteSuccess(state, payload: { questaoId: string }) {
      changeQuestaoItem(state, payload.questaoId, null, { isLoading: false })
    },
    questaoDeleteError(state, payload: { questaoId: string }) {
      changeQuestaoItem(state, payload.questaoId, {}, { isLoading: false })
    },
  },
  {
    thunks: {
      fetchQuestao: (questaoId: string) => async (dispatch) => {
        if (!questaoId) return
        dispatch(questaoEditorStore.actions.questaoFetchStarted({ questaoId }))
        try {
          const questao = await api.Questoes.getById(+questaoId)
          dispatch(questaoEditorStore.actions.questaoFetchSuccess({ questaoId, questao }))
        } catch (error) {
          dispatch(questaoEditorStore.actions.questaoFetchError({ questaoId, erro: error.message }))
        }
      },
      createOrUpdateQuestao: (questao: QuestaoModel, questaoId: string, done) => async (dispatch) => {
        if (!questao) return
        const retornoValidacao = validaQuestao(questao)
        if (!retornoValidacao.valid) {
          return dispatch(questaoEditorStore.actions.questaoCreateOrUpdateError({ questaoId, erro: retornoValidacao.message }))
        }
        dispatch(questaoEditorStore.actions.questaoCreateOrUpdateStarted({ questaoId }))
        try {
          const questaoCreatedOrUpdated = await api.Questoes.createOrUpdate(questao)
          dispatch(questaoEditorStore.actions.questaoCreateOrUpdateSuccess({ questaoId, questao: questaoCreatedOrUpdated }))
          done?.(questaoCreatedOrUpdated)
          toast('Questão atualizada com sucesso')
        } catch (error) {
          dispatch(questaoEditorStore.actions.questaoCreateOrUpdateError({ questaoId, erro: error }))
          toast(error?.message || 'Erro ao atualizar questão')
        }
      },
      deleteQuestao: (questaoId: string, done) => async (dispatch, getState) => {
        try {
          await api.Questoes.delete(questaoId)
          dispatch(questaoEditorStore.actions.questaoDeleteSuccess({ questaoId }))
          dispatch(questaoEditorStore.actions.resetQuestao({ questaoId }))
          done?.()
          toast('Questão excluida')
        } catch (error) {
          dispatch(questaoEditorStore.actions.questaoDeleteError({ questaoId }))
          toast(error?.message || 'Erro ao excluir questão')
        }
      },
    },
  }
)

function validaQuestao(questao: QuestaoModel): { valid: boolean; message?: string } {
  if (!questao.tipoQuestao)
    return { valid: false, message: 'É necessário informar o tipo da questão' }
  if (!questao.local)
    return {
      valid: false,
      message: 'É necessário informar se essa é uma questão de avaliação ou do contéudo',
    }
  if (!questao.moduloId)
    return { valid: false, message: 'É necessário selecionar o módulo dessa questão' }

  if (!questao.questoesAlternativas || !questao.questoesAlternativas.length) {
    return { valid: false, message: 'É necessário informar alternativas' }
  }
  if (questao.questoesAlternativas.length < 2) {
    return { valid: false, message: 'É necessário informar ao menos duas alternativas' }
  }

  switch (questao.tipoQuestao) {
    case TIPO_QUESTAO.MULTIPLA_ESCOLHA:
      return validaQuestaoMultiplaEscolha(questao)
    case TIPO_QUESTAO.RELACIONAR_ITENS:
      return validaQuestaoRelacionarItens(questao)
    case TIPO_QUESTAO.RELACIONAR_ITEM_A_IMAGEM:
      return validaQuestaoRelacionarItemImagem(questao)
    case TIPO_QUESTAO.MULTIPLO_VERDADEIRO_FALSO:
      return validaMultiploVerdadeiroFalso(questao)
    default:
      return { valid: false, message: 'É necessário selecionar um tipo de questão' }
  }
}

function validaQuestaoMultiplaEscolha(questao: QuestaoModel) {
  if (!questao.textoQuestao.trim()) {
    return { valid: false, message: 'É necessário informar o enunciado da questão' }
  }
  if (questao.questoesAlternativas.filter((x) => '' + x.respostaEsperada === 'true').length !== 1) {
    return { valid: false, message: 'É necessário selecionar uma alternativa correta' }
  }
  if (questao.questoesAlternativas.filter((x) => '' + x.respostaEsperada === 'false').length <= 0) {
    return { valid: false, message: 'É necessário que exista ao menos uma alternativa incorreta' }
  }
  if (
    questao.questoesAlternativas.filter(
      (x) => '' + x.respostaEsperada !== 'false' && '' + x.respostaEsperada !== 'true'
    ).length > 0
  ) {
    return {
      valid: false,
      message:
        'Existem alternativas cujas respostas estão configuradas erroneamente. Entre em contato com o suporte',
    }
  }

  return { valid: true }
}

function validaQuestaoRelacionarItens(questao: QuestaoModel) {
  if (questao.questoesAlternativas.filter((x) => !x.conteudoAlternativa).length) {
    return { valid: false, message: 'Todas as alternativas precisam ser preenchidas' }
  }
  return { valid: true }
}

function validaQuestaoRelacionarItemImagem(questao: QuestaoModel) {
  return { valid: true }
}

function validaMultiploVerdadeiroFalso(questao: QuestaoModel) {
  if (questao.questoesAlternativas.filter((x) => !['V', 'F'].includes(x.respostaEsperada)).length) {
    return {
      valid: false,
      message: 'É necessário informar em todas as alternativas se são verdadeiras ou falsas',
    }
  }
  if (questao.questoesAlternativas.filter((x) => !x.conteudoAlternativa).length) {
    return { valid: false, message: 'Todas as alternativas precisam ser preenchidas' }
  }

  return { valid: true }
}

export { questaoEditorStore }
export const { Provider: QuestaoEditorProvider, useDispatch: useQuestaoEditorDispatch, useState: useQuestaoEditorState, useSelector: useQuestaoEditorSelector, actions: questaoEditorActions, thunks: questaoEditorThunks } = questaoEditorStore
